import { useMemo, useRef } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFn = (...args: any[]) => any;

/**
 * Gives a bag of handlers permanently stable identities.
 *
 * A view-model hook naturally declares its handlers as plain functions, which means every one is a
 * new reference on every render. Anything downstream that lists them as a `useCallback` dependency —
 * or receives them as a prop — is then recreated too, and every `memo()` beneath it is defeated. In
 * the Testing chat that cascaded: one streamed activity event re-rendered every turn in the thread,
 * including its plan-preview table.
 *
 * Wrapping the bag returns callbacks whose identity never changes, each delegating to the newest
 * implementation through a ref. Callers see no behavioural difference — the invoked function is
 * always the current one, so no stale closure — but referential equality now holds.
 *
 * Only pass handlers. Values belong in the return object directly, where changing identity is the
 * whole point.
 */
export function useStableHandlers<T extends Record<string, AnyFn>>(handlers: T): T {
	// Updated on every render, read only at call time — so the wrapper never closes over stale state.
	const latest = useRef(handlers);
	latest.current = handlers;

	// Keyed on the SHAPE of the bag, not its values: the set of handler names is fixed for the life
	// of the hook, so the wrappers are built once.
	const names = Object.keys(handlers).sort().join(",");
	return useMemo(() => {
		const stable = {} as T;
		for (const name of Object.keys(latest.current) as Array<keyof T>) {
			stable[name] = ((...args: unknown[]) =>
				latest.current[name](...args)) as T[keyof T];
		}
		return stable;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [names]);
}
